import { EXTRA_CTRL_TYPE } from "@/commons/enums/extra-control-code";
import { EXTRA_SINGAL } from "@/commons/enums/extra-singal";
import { BasicControl, CURSOR_TYPE, CircleT, DYMODE_MODE, EllipseT, Line, Listeners, MOUSE, Plane, PlineT, Point, RegionT, SelectionBox, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode } from "pytha";
import { Vector2, Vector3 } from "three";
import { AddHoleToBoundingWallCommand, BoundingWallT, HoleT } from "tnbimbase";

export class AssembleHoleControl extends BasicControl {
    id = EXTRA_CTRL_TYPE.ASSEMBLE_HOLE;

    needInit: boolean;

    // step:0 选择要开洞的平面
    // step:1 放置洞口
    // step:2 选择轮廓线
    step: number = 0

    layerUuid: string;
    // 选择的要开洞的体
    selectedSolid: BoundingWallT;
    // 选择的要开洞的体外面的平面
    rightFacesMap: { [key: number]: RegionT } = {};
    // 洞口临时图元
    helpHole: HoleT;
    // 布置在了墙的哪个curve上
    boundingWallCurveIndex: number;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    point_down: Point = new Point();
    point_move: Point = new Point();

    capturePoint: Vector3;

    // 洞口要布置的工作平面，局部坐标轴
    workPlane: Plane = new Plane();
    axisZ: Vector3 = new Vector3();
    axisX: Vector3 = new Vector3();


    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        this.needInit = true;

        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();

        this.editor = editor;
        this.listeners = listeners;
        // 钝化夹点
        this.editor.clampControl.enabled = false;
        this.listeners.getSignal(EXTRA_SINGAL.onAssembleHole).add(this.onAssembleHole);
        this._pickBox = new SelectionBox(editor.camera);

        console.log('AssembleHoleControl added')
    }

    dispose(): void {
        this.listeners.getSignal(EXTRA_SINGAL.onAssembleHole).remove(this.onAssembleHole);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.listeners.signals.onKeyMouseConfirm.remove(this.onKeyMouseConfirm);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onKeyMouseConfirm.remove(this.onKeyMouseConfirm);
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;
        this.extraContext.selectContext.forceMeshSelect = false;  //取消强制使用面选择
        this.listeners.signals.onOnePickOnly.dispatch(false);
        if (this.helpHole && this.helpHole.viewObj && this.helpHole.viewObj.parent) {
            this.helpHole.viewObj.parent?.remove(this.helpHole.viewObj);
        }

        console.log('AssembleHoleControl removed')
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    initControl(data: any): void {
        this.onAssembleHole(data.layerUuid, data.hole)
    }

    onAssembleHole = (layerUuid: string, hole: HoleT) => {
        let initData = {
            layerUuid: layerUuid,
            hole: hole,
        }
        this.listeners.signals.setInitControlData.dispatch(initData);

        this.layerUuid = layerUuid;
        if (hole != null) {
            this.helpHole = hole.clone();
        }

        // 派发置空选择集的信号
        this.listeners.signals.emptySelectedEntities.dispatch();
        this.extraContext.dymodeContext.setDymodeTip('选择墙上的平面:');
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.TIPONLY);
        this.extraContext.stmodeContext.setStmode('选择墙上的平面');
        this.listeners.signals.onOnePickOnly.dispatch(true);
        // 激活选择
        this.editor.selectControl.setEnabled(true);
        this.editor.clampControl.hidden = true;
        this.extraContext.selectContext.forceMeshSelect = true;  //强制使用面选择
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.PICK);
        // 订阅确认信号
        this.listeners.signals.onKeyMouseConfirm.add(this.onKeyMouseConfirm);
        // 监听鼠标按下
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    }

    _dymodeActive = false;
    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            this.point_down.set(value.x, value.y, value.z);
            this._dymodeActive = true;
            this.onKeyMouseConfirm();
        }
    }

    onKeyMouseConfirm = () => {
        if (this.step == 0) {
            this.extraContext.stmodeContext.pushTip(`请选择墙上的平面`);
        } else if (this.step == 1) {
            if (!this._dymodeActive) {
                this.listeners.signals.onOpeCommandControlEnd.dispatch();
            } else {
                this.workPlane.projectPoint(this.point_down, this._foot);
                this.point_down.copy(this._foot);
                this.openHole();
            }
        } else if (this.step == 2) {
            this.extraContext.stmodeContext.pushTip(`请选择闭合的轮廓线`);
        }
    }

    private _pointLast = new Point();
    private _rightPline: PlineT = new PlineT();
    // 获取所有的面
    // todo 目前只获得墙面外面的平面
    getSelectedFaces() {
        const boundingWall = this.selectedSolid;
        this._rightPline = boundingWall.basePline.clone();
        if (boundingWall.rightWidth > 0) {
            this._rightPline.offsetToLeft(false, boundingWall.rightWidth);
        }
        this._rightPline._geoCurves.forEach((item, index) => {
            if (item.type == 'Line') {
                let line = item as Line;
                let start = line.getPointAt(0);
                let end = line.getPointAt(1);
                let heightDelta = boundingWall._geo.direction.clone().multiplyScalar(boundingWall._geo.height);
                let topStart = start.clone().add(heightDelta);
                let topEnd = end.clone().add(heightDelta);
                let region = new RegionT([
                    new Line(start, end),
                    new Line(end, topEnd),
                    new Line(topEnd, topStart),
                    new Line(topStart, start),
                ])
                this.rightFacesMap[index] = region;
            }
        })
    }

    private _position = new Point();
    private _dir = new Vector3();
    private _foot = new Point();

    onPointerMove = (event) => {
        if (!!!this.helpHole) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
        this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_move, this.workPlane);
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        this.workPlane.projectPoint(this.point_move, this._foot);
        this.point_move.copy(this._foot);
        this._dir.subVectors(this.point_move, this.helpHole.getCenter(this._position))
        let length = this._dir.length();
        this._dir.normalize();
        this.helpHole.move(this._dir, length);
        this.helpHole.updateViewGeometry();
        this._pointLast.copy(this.point_move);
        this.listeners.signals.needRender.dispatch();
    }

    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        if (event.button !== MOUSE.LEFT) return;
        if (this.step == 0) {
            let selectedEntities = this.editor.selectControl.getSelectedEntityList().filter(entity => !entity.lock && entity.type == "BoundingWallT");
            if (selectedEntities.length > 0) {
                this.editor.selectControl.setEnabled(false);
                this.selectedSolid = selectedEntities[0] as BoundingWallT;
                this.getSelectedFaces();
                const mesh: RegionT = this.getSelectedMesh(event);
                if (mesh == null) {
                    this.extraContext.stmodeContext.pushTip(`请选择墙上的平面`);
                    return;
                }
                // 洞口要放置的面作为工作平面
                // 洞口的方向朝里
                this.workPlane.copy(mesh._geoPolygon).negate();
                // 当前光标在该面上的坐标
                let rect = this.editor.renderer.domElement.getBoundingClientRect();
                let x = (event.clientX - rect.x) / rect.width * 2 - 1;
                let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
                this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down, this.workPlane);
                if (this.helpHole == null) {
                    // 选择轮廓线
                    this.step = 2;
                    // 派发置空选择集的信号
                    this.listeners.signals.emptySelectedEntities.dispatch();
                    this.editor.clampControl.enabled = false;
                    this.editor.clampControl.hidden = true;
                    this.listeners.signals.sendToDymodeTip.dispatch('选择洞口轮廓线:');
                    this.extraContext.stmodeContext.setStmode('选择洞口轮廓线');
                } else {
                    // 移动位置
                    this._dir.subVectors(this.point_down, this.helpHole.getCenter(this._position))
                    let length = this._dir.length();
                    this._dir.normalize();
                    this.helpHole.move(this._dir, length);
                    // 将洞口转至当前工作平面上
                    let normalRotateAxis = this.helpHole._geoPolygon.normal.clone().cross(this.workPlane.normal).normalize();
                    if (Math.abs(this.workPlane.normal.dot(this.helpHole._geoPolygon.normal) + 1) < 1e-6) {
                        normalRotateAxis = this.helpHole._geoPolygon.axisX.clone();
                    }
                    let normalDeltaAngle = this.workPlane.normal.angleTo(this.helpHole._geoPolygon.normal);
                    this.helpHole.rotate(this.point_down, normalRotateAxis, normalDeltaAngle);
                    let tempAxisX = this.helpHole._geoPolygon.axisX;
                    this.axisX.copy(this.workPlane.normal).cross(new Vector3(0, 0, 1)).normalize();
                    let axisXRotateAxis = tempAxisX.clone().cross(this.axisX).normalize();
                    let axisXDeltaAngle = tempAxisX.angleTo(this.axisX);
                    if (axisXRotateAxis.length() < 1e-6 || Math.abs(tempAxisX.dot(this.axisX) + 1) < 1e-6) {
                        axisXRotateAxis = this.workPlane.normal.clone();
                    }
                    this.helpHole.rotate(this.point_down, axisXRotateAxis, axisXDeltaAngle);
                    // 显示临时图元
                    if (this.helpHole && this.helpHole.viewObj && this.helpHole.viewObj.parent) {
                        this.helpHole.viewObj.parent?.remove(this.helpHole.viewObj);
                    }
                    this._pointLast.copy(this.point_down);
                    this.helpHole.render();
                    this.extraContext.sceneContext.scene.add(this.helpHole.viewObj);
                    // 下一步，放置洞口
                    this.step = 1;
                    this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
                    this.listeners.signals.sendToDymodeTip.dispatch('指定开洞位置:');
                    this.extraContext.stmodeContext.setStmode('指定开洞位置');
                    this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
                    this.listeners.signals.needCapture.dispatch();
                    this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
                    this.listeners.signals.onPointCapture.add(this.onPointCapture);
                    // 钝化选择
                    this.listeners.signals.emptySelectedEntities.dispatch();
                    this.editor.selectControl.setEnabled(false);
                    // 监听鼠标移动
                    this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
                }
            }
        } else if (this.step == 1) {
            // 指定了洞口位置
            let rect = this.editor.renderer.domElement.getBoundingClientRect();
            let x = (event.clientX - rect.x) / rect.width * 2 - 1;
            let y = -(event.clientY - rect.y) / rect.height * 2 + 1;
            this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down, this.workPlane);
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.workPlane.projectPoint(this.point_down, this._foot);
            this.point_down.copy(this._foot);
            this.openHole();
        } else if (this.step == 2) {
            let selectedEntities = this.editor.selectControl.getSelectedEntityList().filter(entity => !entity.lock && ((entity.type == "PlineT" && (entity as PlineT).loop) || entity.type == "CircleT" || entity.type == 'EllipseT'));
            if (selectedEntities.length > 0) {
                // 选择了轮廓线
                let selectedRing = selectedEntities[0];
                switch (selectedRing.type) {
                    case "PlineT":
                        let pline = selectedRing as PlineT;
                        this.helpHole = new HoleT(pline._geoCurves);
                        break;
                    case "CircleT":
                        let circle = selectedRing as CircleT;
                        this.helpHole = new HoleT([circle._geoCircle]);
                        break;
                    case "EllipsisT":
                        let ellipse = selectedRing as EllipseT;
                        this.helpHole = new HoleT([ellipse._geoEllipse]);
                        break;
                }
                this.workPlane.projectPoint(this.helpHole.getCenter(this._position), this._foot);
                this.point_down.copy(this._foot);
                // 移动位置
                this._dir.subVectors(this.point_down, this.helpHole.getCenter(this._position))
                let length = this._dir.length();
                this._dir.normalize();
                this.helpHole.move(this._dir, length);
                // 将洞口转至当前工作平面上
                let normalRotateAxis = this.helpHole._geoPolygon.normal.clone().cross(this.workPlane.normal).normalize();
                if (Math.abs(this.workPlane.normal.dot(this.helpHole._geoPolygon.normal) + 1) < 1e-6) {
                    normalRotateAxis = this.helpHole._geoPolygon.axisX.clone();
                }
                let normalDeltaAngle = this.workPlane.normal.angleTo(this.helpHole._geoPolygon.normal);
                this.helpHole.rotate(this.point_down, normalRotateAxis, normalDeltaAngle);
                this.openHole();
            }
        }
    }

    openHole = () => {
        this._dir.subVectors(this.point_down, this.helpHole.getCenter(this._position))
        let length = this._dir.length();
        this._dir.normalize();
        this.helpHole.move(this._dir, length);
        this.helpHole.updateViewGeometry();
        this._pointLast.copy(this.point_move);
        let hole: HoleT = this.helpHole.clone();
        hole.layerUuid = this.layerUuid;
        hole.isColorByLayer = true;
        hole.boundingWallCurveIndex = this.boundingWallCurveIndex;
        this.editor.history.execute(new AddHoleToBoundingWallCommand((this.selectedSolid as BoundingWallT), hole));
        this.listeners.signals.onOpeCommandControlEnd.dispatch();
    }

    private _pickBox: SelectionBox

    // 获得选中的面
    getSelectedMesh(event) {
        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let PickEpsilon = this.extraContext.editStatusContext.PickEpsilon;
        let top_left_x = event.clientX - PickEpsilon;
        let top_left_y = event.clientY - PickEpsilon;
        let bottom_right_x = event.clientX + PickEpsilon;
        let bottom_right_y = event.clientY + PickEpsilon;

        let start_x = (top_left_x - rect.x) / rect.width * 2 - 1;
        let start_y = -(top_left_y - rect.y) / rect.height * 2 + 1;
        let end_x = (bottom_right_x - rect.x) / rect.width * 2 - 1;
        let end_y = -(bottom_right_y - rect.y) / rect.height * 2 + 1;
        this._pickBox.update(new Vector2(start_x, start_y), new Vector2(end_x, end_y))
        let rightSelected = [];
        for (let key in this.rightFacesMap) {
            let entity = this.rightFacesMap[key];
            if (!!entity && !!entity?.rangeShoot(this._pickBox._frustum)) {
                rightSelected.push(key);
            }
        }
        if (rightSelected.length <= 0) return null;
        // 寻找离相机最近的面
        const cameraPosition = this.extraContext.getCurrentViewEditor().camera.position;
        let region = null;
        let boundingWallCurveIndex = null;
        let shortestDistance = Infinity;
        for (let i = 0; i < rightSelected.length; i++) {
            let key = rightSelected[i]
            const distance = this.rightFacesMap[key].getBoundingBox().distanceToPoint(cameraPosition)
            if (distance < shortestDistance) {
                region = this.rightFacesMap[key];
                shortestDistance = distance;
                boundingWallCurveIndex = key;
            }
        }
        this.boundingWallCurveIndex = +boundingWallCurveIndex;
        return region;
    }
}